import React, { useState, useEffect } from 'react';
import { FaSave, FaUndo, FaShieldAlt, FaCheck } from 'react-icons/fa';
import { supabase } from '../services/supabase';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

function TwoFactorSection() {
  const { admin, getAdminToken } = useAuth();
  const [enabled, setEnabled] = useState(!!admin?.totp_enabled);
  const [setupData, setSetupData] = useState(null); // { secret, otpauthUrl }
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const call = (body) =>
    supabase.functions.invoke('admin-2fa', { body: { admin_token: getAdminToken(), ...body } });

  const startSetup = async () => {
    setMsg(''); setBusy(true);
    const { data, error } = await call({ action: 'setup' });
    setBusy(false);
    if (error || data?.error) return setMsg('❌ Xəta baş verdi');
    setSetupData(data);
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setMsg(''); setBusy(true);
    const { data, error } = await call({ action: 'confirm', secret: setupData.secret, code });
    setBusy(false);
    if (error || data?.error) return setMsg('❌ Kod səhvdir, yenidən cəhd edin');
    setEnabled(true);
    setSetupData(null);
    setCode('');
    setMsg('✅ 2FA aktivləşdirildi!');
  };

  const disable2fa = async (e) => {
    e.preventDefault();
    setMsg(''); setBusy(true);
    const { data, error } = await call({ action: 'disable', code: disableCode });
    setBusy(false);
    if (error || data?.error) return setMsg('❌ Kod səhvdir');
    setEnabled(false);
    setDisableCode('');
    setMsg('2FA söndürüldü');
  };

  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>🔐 İki Addımlı Doğrulama (2FA)</h2>

      {enabled ? (
        <>
          <p style={{ color: '#2E7D32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaCheck /> 2FA aktivdir
          </p>
          <form onSubmit={disable2fa} style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
            <input
              type="text" inputMode="numeric" maxLength={6} placeholder="Söndürmək üçün kod"
              value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
              style={{ ...styles.input, width: 160 }}
            />
            <button type="submit" disabled={busy || disableCode.length !== 6} style={styles.btnReset}>
              2FA-nı söndür
            </button>
          </form>
        </>
      ) : setupData ? (
        <div>
          <p style={{ fontSize: 14, color: '#666' }}>
            Google Authenticator (və ya bənzər bir app) ilə bu kodu skan et, sonra app-da göstərilən 6 rəqəmi yaz:
          </p>
          <img
            alt="2FA QR kod"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.otpauthUrl)}`}
            style={{ margin: '12px 0' }}
          />
          <p style={{ fontSize: 12, color: '#999' }}>Əl ilə daxil etmək üçün: <code>{setupData.secret}</code></p>
          <form onSubmit={confirmSetup} style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
            <input
              type="text" inputMode="numeric" maxLength={6} placeholder="000000"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus style={{ ...styles.input, width: 160, textAlign: 'center', letterSpacing: 4 }}
            />
            <button type="submit" disabled={busy || code.length !== 6} style={styles.btnSave}>
              Təsdiqlə və aktivləşdir
            </button>
          </form>
        </div>
      ) : (
        <button onClick={startSetup} disabled={busy} style={styles.btnSave}>
          <FaShieldAlt /> 2FA aktivləşdir
        </button>
      )}

      {msg && <p style={{ marginTop: 12, fontSize: 14 }}>{msg}</p>}
    </div>
  );
}

function SettingsPage() {
  const { admin } = useAuth();
  const [settings, setSettings] = useState({
    // Gamification
    pointsMultiplier: 1.0,
    dailySpinLimit: 1,
    registrationBonus: 100,
    dailyLoginBonus: 10,
    checkinBonus: 50,
    
    // Badge Thresholds
    socialButterflyThreshold: 10,
    explorerThreshold: 5,
    loyalCustomerThreshold: 10,
    earlyBirdThreshold: 5,
    weekendWarriorThreshold: 5,
    
    // Subscription
    defaultSubscriptionPrice: 100,
    trialPeriodDays: 14,
    renewalReminderDays: 30,
    
    // Notifications
    emailAlertsEnabled: true,
    fraudAlertsEnabled: true,
    revenueAlertsEnabled: true,
    
    // Fraud Detection
    gpsTolerance: 20,
    qrScanLimit: 5,
    rapidCheckinLimit: 5,
    rapidCheckinPeriod: 10,
    autoBanEnabled: false,
  });

  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await supabase.from('system_settings').select('key, value');
      if (data?.length) {
        const map = Object.fromEntries(data.map(r => [r.key, r.value]));
        setSettings(prev => ({ ...prev, ...Object.fromEntries(Object.entries(prev).map(([k]) => [k, map[k] !== undefined ? (typeof prev[k] === 'boolean' ? map[k] === 'true' : typeof prev[k] === 'number' ? Number(map[k]) : map[k]) : prev[k]])) }));
      } else {
        // Fallback to localStorage cache
        const cached = localStorage.getItem('system_settings');
        if (cached) {
          try { setSettings(prev => ({ ...prev, ...JSON.parse(cached) })); } catch (_) {}
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Fallback to localStorage
      const cached = localStorage.getItem('system_settings');
      if (cached) {
        try { setSettings(prev => ({ ...prev, ...JSON.parse(cached) })); } catch (_) {}
      }
    }
  };

  const handleChange = (field, value) => {
    setSettings({
      ...settings,
      [field]: value,
    });
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveSuccess(false);

    try {
      // Upsert all settings to system_settings table
      const rows = Object.entries(settings).map(([key, value]) => ({ key, value: String(value) }));
      const { error: upsertError } = await supabase
        .from('system_settings')
        .upsert(rows, { onConflict: 'key' });

      if (upsertError) {
        console.error('Supabase upsert error:', upsertError);
      }

      // Keep localStorage as cache/fallback
      localStorage.setItem('system_settings', JSON.stringify(settings));

      // Log yaz
      await adminService.logAction(
        admin.id,
        'update_settings',
        'system',
        null,
        { settings }
      );

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Xəta baş verdi!');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Parametrləri sıfırlamaq istədiyinizdən əminsiniz?')) {
      return;
    }

    setSettings({
      pointsMultiplier: 1.0,
      dailySpinLimit: 1,
      registrationBonus: 100,
      dailyLoginBonus: 10,
      checkinBonus: 50,
      socialButterflyThreshold: 10,
      explorerThreshold: 5,
      loyalCustomerThreshold: 10,
      earlyBirdThreshold: 5,
      weekendWarriorThreshold: 5,
      defaultSubscriptionPrice: 100,
      trialPeriodDays: 14,
      renewalReminderDays: 30,
      emailAlertsEnabled: true,
      fraudAlertsEnabled: true,
      revenueAlertsEnabled: true,
      gpsTolerance: 20,
      qrScanLimit: 5,
      rapidCheckinLimit: 5,
      rapidCheckinPeriod: 10,
      autoBanEnabled: false,
    });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>⚙️ Sistem Parametrləri</h1>
          <p style={styles.subtitle}>Sistem konfiqurasiyasını idarə edin</p>
        </div>
        <div style={styles.headerActions}>
          <button onClick={handleReset} style={styles.btnReset}>
            <FaUndo /> Sıfırla
          </button>
          <button onClick={handleSave} disabled={loading} style={styles.btnSave}>
            <FaSave /> {loading ? 'Yadda saxlanılır...' : 'Yadda Saxla'}
          </button>
        </div>
      </div>

      {saveSuccess && (
        <div style={styles.successMessage}>
          ✅ Parametrlər uğurla yadda saxlanıldı!
        </div>
      )}

      <TwoFactorSection />

      {/* Gamification Settings */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🎮 Gamification Parametrləri</h2>
        <div style={styles.grid}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Points Multiplier
              <span style={styles.hint}>Xalların çarpımı (1.0 = normal)</span>
            </label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              max="5"
              value={settings.pointsMultiplier}
              onChange={(e) => handleChange('pointsMultiplier', parseFloat(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Gündəlik Spin Limiti
              <span style={styles.hint}>Hər user günə neçə dəfə spin edə bilər</span>
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={settings.dailySpinLimit}
              onChange={(e) => handleChange('dailySpinLimit', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Qeydiyyat Bonusu
              <span style={styles.hint}>İlk qeydiyyat zamanı verilən xal</span>
            </label>
            <input
              type="number"
              min="0"
              value={settings.registrationBonus}
              onChange={(e) => handleChange('registrationBonus', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Gündəlik Login Bonusu
              <span style={styles.hint}>Hər gün ilk login zamanı</span>
            </label>
            <input
              type="number"
              min="0"
              value={settings.dailyLoginBonus}
              onChange={(e) => handleChange('dailyLoginBonus', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Check-in Bonusu
              <span style={styles.hint}>Hər check-in üçün xal</span>
            </label>
            <input
              type="number"
              min="0"
              value={settings.checkinBonus}
              onChange={(e) => handleChange('checkinBonus', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>
        </div>
      </div>

      {/* Badge Thresholds */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🏆 Badge Threshold-ları</h2>
        <div style={styles.grid}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Social Butterfly
              <span style={styles.hint}>Neçə friend</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.socialButterflyThreshold}
              onChange={(e) => handleChange('socialButterflyThreshold', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Explorer
              <span style={styles.hint}>Neçə fərqli business</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.explorerThreshold}
              onChange={(e) => handleChange('explorerThreshold', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Loyal Customer
              <span style={styles.hint}>Eyni business-ə neçə dəfə</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.loyalCustomerThreshold}
              onChange={(e) => handleChange('loyalCustomerThreshold', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Early Bird
              <span style={styles.hint}>Səhər 09:00-dan əvvəl neçə dəfə</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.earlyBirdThreshold}
              onChange={(e) => handleChange('earlyBirdThreshold', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Weekend Warrior
              <span style={styles.hint}>Həftəsonu neçə dəfə</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.weekendWarriorThreshold}
              onChange={(e) => handleChange('weekendWarriorThreshold', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>
        </div>
      </div>

      {/* Subscription Settings */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>💳 Subscription Parametrləri</h2>
        <div style={styles.grid}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Default Qiymət
              <span style={styles.hint}>Aylıq subscription (₼)</span>
            </label>
            <input
              type="number"
              min="0"
              value={settings.defaultSubscriptionPrice}
              onChange={(e) => handleChange('defaultSubscriptionPrice', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Trial Müddəti
              <span style={styles.hint}>Pulsuz sınaq müddəti (gün)</span>
            </label>
            <input
              type="number"
              min="0"
              value={settings.trialPeriodDays}
              onChange={(e) => handleChange('trialPeriodDays', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Renewal Reminder
              <span style={styles.hint}>Neçə gün qalıb xəbərdarlıq (gün)</span>
            </label>
            <input
              type="number"
              min="0"
              value={settings.renewalReminderDays}
              onChange={(e) => handleChange('renewalReminderDays', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🔔 Bildiriş Parametrləri</h2>
        <div style={styles.checkboxGrid}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings.emailAlertsEnabled}
              onChange={(e) => handleChange('emailAlertsEnabled', e.target.checked)}
              style={styles.checkbox}
            />
            Email Alerts
            <span style={styles.checkboxHint}>Email bildirişləri göndər</span>
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings.fraudAlertsEnabled}
              onChange={(e) => handleChange('fraudAlertsEnabled', e.target.checked)}
              style={styles.checkbox}
            />
            Fraud Alerts
            <span style={styles.checkboxHint}>Fraud bildirişləri göndər</span>
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings.revenueAlertsEnabled}
              onChange={(e) => handleChange('revenueAlertsEnabled', e.target.checked)}
              style={styles.checkbox}
            />
            Revenue Alerts
            <span style={styles.checkboxHint}>Maliyyə bildirişləri göndər</span>
          </label>
        </div>
      </div>

      {/* Fraud Detection Settings */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🚨 Fraud Detection Parametrləri</h2>
        <div style={styles.grid}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              GPS Tolerance
              <span style={styles.hint}>GPS və IP arası maksimum məsafə (km)</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.gpsTolerance}
              onChange={(e) => handleChange('gpsTolerance', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              QR Scan Limiti
              <span style={styles.hint}>Saatda maksimum scan sayı</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.qrScanLimit}
              onChange={(e) => handleChange('qrScanLimit', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Rapid Check-in Limiti
              <span style={styles.hint}>Maksimum check-in sayı</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.rapidCheckinLimit}
              onChange={(e) => handleChange('rapidCheckinLimit', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Rapid Check-in Müddəti
              <span style={styles.hint}>Neçə dəqiqə ərzində (dəqiqə)</span>
            </label>
            <input
              type="number"
              min="1"
              value={settings.rapidCheckinPeriod}
              onChange={(e) => handleChange('rapidCheckinPeriod', parseInt(e.target.value))}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.autoBanEnabled}
                onChange={(e) => handleChange('autoBanEnabled', e.target.checked)}
                style={styles.checkbox}
              />
              Auto-Ban Aktiv
              <span style={styles.checkboxHint}>
                ⚠️ XƏBƏRDARLIQ: Avtomatik ban aktivdir
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Save Button (Bottom) */}
      <div style={styles.bottomActions}>
        <button onClick={handleReset} style={styles.btnReset}>
          <FaUndo /> Sıfırla
        </button>
        <button onClick={handleSave} disabled={loading} style={styles.btnSave}>
          <FaSave /> {loading ? 'Yadda saxlanılır...' : 'Yadda Saxla'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  headerActions: {
    display: 'flex',
    gap: '15px',
  },
  btnSave: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  btnReset: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  successMessage: {
    padding: '15px 20px',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'center',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    marginBottom: '25px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #F0F0F0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '25px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A1A2E',
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  hint: {
    fontSize: '12px',
    color: '#999',
    fontWeight: '400',
  },
  input: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border 0.3s',
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
  },
  checkboxLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A1A2E',
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  checkboxHint: {
    fontSize: '12px',
    color: '#999',
    fontWeight: '400',
    marginTop: '3px',
  },
  bottomActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '15px',
    marginTop: '30px',
  },
};

export default SettingsPage;